import { SectionHeader, Stat, Tag } from "./primitives";
import { shortAddr } from "./mkt-format";
import { useT } from "../../i18n";
import type { OwnerAudit } from "../../lib/marketplace-api";

const SUPPLY_LABELS: { key: keyof OwnerAudit["supply"]; labelKey: string }[] = [
  { key: "active_listings", labelKey: "marketplace.owner.supplyActiveListings" },
  { key: "sold_listings", labelKey: "marketplace.owner.supplySoldListings" },
  { key: "open_offers", labelKey: "marketplace.owner.supplyOpenOffers" },
  { key: "open_auctions", labelKey: "marketplace.owner.supplyOpenAuctions" },
  { key: "pending_settlements", labelKey: "marketplace.owner.supplyPendingSettlements" },
  { key: "recorded_acquisitions", labelKey: "marketplace.owner.supplyPaidAcquisitions" },
];

export function SupplyIntegrityPanel({ audit }: { audit: OwnerAudit }) {
  const t = useT();
  const ok = audit.integrity.no_free_grant_ok;
  return (
    <section data-testid="owner-integrity-panel">
      <SectionHeader
        index="01"
        title={t("marketplace.owner.integrityTitle")}
        action={
          <span
            data-testid="owner-integrity-status"
            data-ok={ok ? "true" : "false"}
            className={
              ok
                ? "font-mono text-[10.5px] uppercase tracking-[0.16em] text-neon-teal"
                : "font-mono text-[10.5px] uppercase tracking-[0.16em] text-state-danger"
            }
          >
            {ok ? t("marketplace.owner.noFreeGrant") : t("marketplace.owner.grantAnomaly")}
          </span>
        }
      />
      <div className="neon-glass rounded-xl border border-border-divider p-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          {SUPPLY_LABELS.map((s) => (
            <Stat key={s.key} label={t(s.labelKey)} testId={`owner-supply-${s.key}`}>
              {audit.supply[s.key]}
            </Stat>
          ))}
        </div>
        <div className="pt-4 border-t border-border-divider">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-text-muted">
              {t("marketplace.owner.zeroPriceGrants")}
            </span>
            <Tag tone={audit.integrity.zero_price_grant_count ? "coral" : "muted"} testId="owner-integrity-zerocount">
              {audit.integrity.zero_price_grant_count}
            </Tag>
          </div>
          {audit.integrity.zero_price_grants.length > 0 && (
            <div className="mt-3 divide-y divide-border-divider">
              {audit.integrity.zero_price_grants.map((g, i) => (
                <div
                  key={`${String(g.parcel_id)}-${i}`}
                  data-testid="owner-integrity-grant-row"
                  className="flex items-center justify-between py-2 font-mono text-[11px]"
                >
                  <span className="text-text-primary">{t("marketplace.common.parcelNumber", { id: String(g.parcel_id) })}</span>
                  <span className="text-neon-coral">
                    {shortAddr(g.owner)} · {g.kind}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
